import React, { useState, useEffect, useRef } from 'react';
import { FunnelIcon, XMarkIcon } from '@heroicons/react/24/outline';

const ChevronDownIcon = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={2.5}
    stroke="currentColor"
    className={className}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
  </svg>
);

const CustomSelect = ({ value, onChange, options, placeholder, disabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedOption = options.find(opt => opt.value === value);

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-2 py-1 text-xs border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white text-left text-gray-700 shadow-sm hover:border-gray-400 transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed"
      >
        <span className="truncate pr-1">{selectedOption ? selectedOption.label : placeholder}</span>
        <ChevronDownIcon className={`h-3 w-3 text-gray-400 transition-transform duration-200 shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto left-0 right-0">
          <ul className="py-0.5 text-xs text-gray-700">
            {options.map((opt) => (
              <li key={opt.value}>
                <button
                  type="button"
                  onClick={() => {
                    onChange(opt.value);
                    setIsOpen(false);
                  }}
                  className={`w-full text-left px-2.5 py-1.5 hover:bg-gray-50 truncate transition-colors ${
                    value === opt.value ? 'bg-blue-50 text-blue-600 font-semibold' : ''
                  }`}
                >
                  {opt.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const SimpleLeadFilter = ({ onFilterChange, leads, employees = [] }) => {
  const [filterType, setFilterType] = useState('');
  const [filterValue, setFilterValue] = useState('');
  const [activeFilters, setActiveFilters] = useState([]);
  const [leadStatuses, setLeadStatuses] = useState([]);
  const [leadSources, setLeadSources] = useState([]);
  const [leadTitles, setLeadTitles] = useState([]);

  useEffect(() => {
    loadLeadStatuses();
    loadLeadSources();
    loadLeadTitles();
  }, []);

  const loadLeadStatuses = async () => {
    try {
      const { leadsApi } = await import('../config/api');
      const response = await leadsApi.get('/lead-statuses');
      if (response.data.success) {
        setLeadStatuses(response.data.statuses || []);
      }
    } catch (error) {
      console.error('Error loading statuses:', error);
    }
  };

  const loadLeadSources = async () => {
    try {
      const { leadsApi } = await import('../config/api');
      const response = await leadsApi.get('/lead-sources');
      if (response.data.success) {
        setLeadSources(response.data.sources || []);
      }
    } catch (error) {
      console.error('Error loading sources:', error);
    }
  };

  const loadLeadTitles = async () => {
    try {
      const { leadsApi } = await import('../config/api');
      const response = await leadsApi.get('/lead-titles');
      if (response.data.success) {
        setLeadTitles(response.data.titles || []);
      }
    } catch (error) {
      console.error('Error loading titles:', error);
    }
  };

  const filterTypes = [
    { value: 'employee', label: 'Employee' },
    { value: 'title', label: 'Title' },
    { value: 'status', label: 'Status' },
    { value: 'source', label: 'Source' },
    { value: 'date', label: 'Date' },
  ];

  const getFilterOptions = () => {
    switch (filterType) {
      case 'employee':
        return employees.map(emp => ({
          value: emp._id || emp.id,
          label: emp.name || emp.email
        }));
      
      case 'title':
        return leadTitles.map(title => ({
          value: title.name,
          label: title.name
        }));
      
      case 'status':
        return leadStatuses.map(status => ({
          value: status.name,
          label: status.label
        }));
      
      case 'source':
        return leadSources.map(source => ({
          value: source.name,
          label: source.label
        }));
      
      case 'date':
        return [
          { value: 'today', label: 'Today' },
          { value: 'yesterday', label: 'Yesterday' },
          { value: 'last7days', label: 'Last 7 Days' },
          { value: 'last30days', label: 'Last 30 Days' },
          { value: 'thisMonth', label: 'This Month' },
          { value: 'lastMonth', label: 'Last Month' }
        ];
      
      default:
        return [];
    }
  };

  const handleAddFilter = (selectedValue) => {
    const valToUse = selectedValue || filterValue;
    if (!filterType || !valToUse) return;

    const filterTypeLabel = filterTypes.find(ft => ft.value === filterType)?.label || filterType;
    const filterValueLabel = getFilterOptions().find(opt => opt.value === valToUse)?.label || valToUse;

    const newFilter = {
      id: Date.now(),
      type: filterType,
      value: valToUse,
      typeLabel: filterTypeLabel,
      valueLabel: filterValueLabel
    };

    const updatedFilters = [...activeFilters, newFilter];
    setActiveFilters(updatedFilters);
    
    // Reset selections
    setFilterType('');
    setFilterValue('');

    // Notify parent
    onFilterChange(updatedFilters);
  };

  const handleRemoveFilter = (filterId) => {
    const updatedFilters = activeFilters.filter(f => f.id !== filterId);
    setActiveFilters(updatedFilters);
    onFilterChange(updatedFilters);
  };

  const handleClearAll = () => {
    setActiveFilters([]);
    setFilterType('');
    setFilterValue('');
    onFilterChange([]);
  };

  // Helper arrays for custom select
  const filterTypeOptionsDesktop = [
    { value: '', label: '1. Select Type...' },
    ...filterTypes
  ];

  const filterTypeOptionsMobile = [
    { value: '', label: 'Filter Type...' },
    ...filterTypes
  ];

  const filterValueOptions = [
    { value: '', label: filterType ? 'Select value...' : 'Select type first' },
    ...getFilterOptions()
  ];

  return (
    <>
      {/* ── Desktop Layout: single inline row ─────────────────── */}
      <div className="hidden md:flex items-center gap-2">
        {/* Filter Type */}
        <div className="w-36">
          <CustomSelect
            value={filterType}
            onChange={(val) => {
              setFilterType(val);
              setFilterValue('');
            }}
            options={filterTypeOptionsDesktop}
            placeholder="1. Select Type..."
          />
        </div>

        {/* Filter Value */}
        <div className="w-36">
          <CustomSelect
            value={filterValue}
            onChange={(val) => {
              setFilterValue(val);
              if (val) handleAddFilter(val);
            }}
            disabled={!filterType}
            options={filterValueOptions}
            placeholder={filterType ? 'Select value...' : 'Select type first'}
          />
        </div>

        {/* Active filter badge + clear */}
        {activeFilters.length > 0 && (
          <>
            <div className="flex items-center gap-1 px-2 py-1 bg-blue-50 border border-blue-200 rounded text-xs">
              <FunnelIcon className="h-3 w-3 text-blue-600" />
              <span className="text-blue-700 font-medium">{activeFilters.length}</span>
            </div>
            <button
              onClick={handleClearAll}
              className="text-xs text-red-600 hover:text-red-700 font-medium whitespace-nowrap"
            >
              Clear All
            </button>
          </>
        )}
      </div>

      {/* ── Mobile Layout: compact 2-col grid row ─────────────── */}
      <div className="md:hidden w-full flex flex-col gap-1.5">
        <div className="grid grid-cols-2 gap-1.5">
          {/* Filter Type */}
          <CustomSelect
            value={filterType}
            onChange={(val) => {
              setFilterType(val);
              setFilterValue('');
            }}
            options={filterTypeOptionsMobile}
            placeholder="Filter Type..."
          />

          {/* Filter Value */}
          <CustomSelect
            value={filterValue}
            onChange={(val) => {
              setFilterValue(val);
              if (val) handleAddFilter(val);
            }}
            disabled={!filterType}
            options={filterValueOptions}
            placeholder={filterType ? 'Select value...' : 'Select type first'}
          />
        </div>

        {/* Active filter count + clear — only when filters active */}
        {activeFilters.length > 0 && (
          <div className="flex items-center justify-between px-2 py-1.5 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center gap-1.5">
              <FunnelIcon className="h-3.5 w-3.5 text-blue-600" />
              <span className="text-blue-700 text-xs font-semibold">
                {activeFilters.length} filter{activeFilters.length > 1 ? 's' : ''} active
              </span>
            </div>
            <button
              onClick={handleClearAll}
              className="text-xs text-red-600 hover:text-red-700 font-semibold"
            >
              Clear All
            </button>
          </div>
        )}
      </div>

      {/* ── Active Filter Tags (shared across both views) ────── */}
      {activeFilters.length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {activeFilters.map(filter => (
            <div
              key={filter.id}
              className="inline-flex items-center bg-blue-50 border border-blue-200 rounded-full px-2.5 py-0.5 text-xs"
            >
              <span className="text-blue-700 font-semibold">{filter.typeLabel}:</span>
              <span className="text-blue-900 ml-1">{filter.valueLabel}</span>
              <button
                onClick={() => handleRemoveFilter(filter.id)}
                className="ml-1.5 text-blue-500 hover:text-red-600 transition-colors"
              >
                <XMarkIcon className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export default SimpleLeadFilter;
